import { Currency, TCurrencyStrings } from "@typed/entities/Currency"

/**
 * Price template ported 1:1 from iOS.
 * iOS sources of truth:
 * - Sources/Services/TemplateService.swift (PDFPriceTemplate)
 * - Sources/Entities/Model/PDF/WorkPDFPageDataModel+App.swift (priceFormatted, price)
 * - Sources/Review/Extensions/String+Extension+App.swift (toCurrency, en_GB decimal grouping)
 */

export const defaultPriceTemplate = "{{currency.symbol}}{{amount.formatted}}"

// The literal in org templates that gets swapped for the localized suffix
export const taxMessageLiteral = "plus applicable taxes"

export function getCurrencyParts(currency: TCurrencyStrings | "UNKNOWN"): { symbol: string; code: string } {
	if (currency === "UNKNOWN" || !(currency in Currency)) {
		return { symbol: "", code: "" }
	}
	const value: string = Currency[currency as TCurrencyStrings]
	const spaceIdx = value.lastIndexOf(" ")
	return { symbol: value.slice(0, spaceIdx), code: value.slice(spaceIdx + 1) }
}

// iOS String.integerStr: strip commas, format as integer ("%.0f" rounds)
export function integerStr(value: string): string {
	const parsed = parseFloat(value.replace(/,/g, ""))
	return String(Math.round(isNaN(parsed) ? 0 : parsed))
}

// iOS String.toCurrency(): en_GB decimal style = comma thousands separators
export function toCurrencyString(value: string): string {
	const int = parseInt(integerStr(value), 10)
	if (isNaN(int)) {
		return value
	}
	return int.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

/**
 * iOS WorkPDFPageDataModel init: price = "" when the integer amount is 0,
 * otherwise thousands-formatted.
 */
export function formatRetailPrice(retailPrice: string): string {
	const int = parseInt(integerStr(retailPrice), 10)
	if (!int) {
		return ""
	}
	return toCurrencyString(retailPrice)
}

/**
 * iOS PDFPriceTemplate.populatedTemplate + priceFormatted.
 * `template` comes from org `pdf_price_template` ("" falls back to the default).
 * `taxMessage` is the localized replacement for "plus applicable taxes".
 */
export function populatePriceTemplate(
	template: string,
	symbol: string,
	code: string,
	formattedAmount: string,
	taxMessage: string
): string {
	if (formattedAmount === "") {
		return ""
	}
	const t = template || defaultPriceTemplate
	return t
		.replace(/\{\{currency\.symbol\}\}/g, symbol)
		.replace(/\{\{currency\.code\}\}/g, code)
		.replace(/\{\{amount\.formatted\}\}/g, formattedAmount)
		.replace(new RegExp(taxMessageLiteral, "g"), taxMessage)
}
